import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, X, Image as ImageIcon, Package, ArrowRightLeft, Trash2, RefreshCcw, DollarSign, Recycle } from "lucide-react";
import { fileUrl, brl } from "../lib";
import { pickErrorMsg } from "../lib/feedback";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authApi = (method, path, body) => {
  const token = localStorage.getItem("cps_token");
  return axios({
    method, url: `${API}${path}`, data: body,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  }).then(r => r.data);
};

const STATUS_LBL = {
  disponivel: { lbl: "Disponível", color: "var(--verde-2)", bg: "rgba(58,90,74,0.18)" },
  reservada: { lbl: "Reservada", color: "var(--gold)", bg: "rgba(184,147,90,0.15)" },
  transferida: { lbl: "Transferida", color: "#9da3aa", bg: "rgba(157,163,170,0.14)" },
  vendida: { lbl: "Vendida", color: "#d4ac5a", bg: "rgba(212,172,90,0.16)" },
  descartada: { lbl: "Descartada", color: "#c47a7a", bg: "rgba(196,122,122,0.14)" },
};

const SETOR_CORES = [
  { v: "#b8935a", l: "Setor A (gold)" },
  { v: "#3a5a4a", l: "Setor B (verde)" },
  { v: "#9b8e7e", l: "Setor C (taupe)" },
  { v: "#c47a7a", l: "Setor D (vermelho)" },
  { v: "#5a6f8a", l: "Setor E (azul)" },
];

function StatusBadge({ status }) {
  const m = STATUS_LBL[status] || STATUS_LBL.disponivel;
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", border: `1px solid ${m.color}50`, background: m.bg, color: m.color, fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase" }}>
      {m.lbl}
    </span>
  );
}

/* =========================================================================
 * SOBRAS PANEL — Embedded inside Obra detail
 * ========================================================================= */
export function SobrasPanel({ obra, canEdit }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [transferring, setTransferring] = useState(null);
  const [obras, setObras] = useState([]);

  const load = useCallback(() => {
    authApi("get", `/obras/${obra.id}/sobras`).then(r => setItems(r.items || [])).catch(() => {});
  }, [obra.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    authApi("get", "/obras").then(setObras).catch(() => {});
  }, []);

  const del = async (id) => {
    if (!window.confirm("Excluir esta sobra?")) return;
    await authApi("delete", `/obras/${obra.id}/sobras/${id}`); load();
  };

  return (
    <div className="panel" style={{ padding: 24, marginBottom: 20 }} data-testid="sobras-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}><Recycle size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--gold)" }} />Estoque de Sobras</div>
          <h3 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 22, margin: 0 }}>Peças <em>reaproveitáveis</em></h3>
        </div>
        {canEdit && (
          <button className="btn" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="btn-new-sobra">
            <Plus size={13} />Cadastrar sobra
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="empty" style={{ padding: 28, fontSize: 13, color: "var(--ink-3)", fontStyle: "italic", textAlign: "center" }}>
          Nenhuma sobra cadastrada nesta obra. Após o corte das chapas, registre as peças que sobraram para reaproveitar em outras obras ou vender.
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl" data-testid="sobras-table">
            <thead>
              <tr>
                <th>Foto</th>
                <th>Material</th>
                <th>Tipo</th>
                <th style={{ textAlign: "center" }}>Dimensões / m²</th>
                <th>Marmorista</th>
                <th>Local</th>
                <th>Status</th>
                <th>Valor</th>
                {canEdit && <th>Ações</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id} data-testid={`sobra-row-${s.id}`}>
                  <td>
                    {s.foto_urls?.[0] ? (
                      <div style={{ width: 50, height: 38, backgroundImage: `url(${fileUrl(s.foto_urls[0])})`, backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--gold-3)" }} />
                    ) : <div style={{ width: 50, height: 38, background: "var(--bg-3)", border: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "center" }}><ImageIcon size={14} color="var(--ink-3)" /></div>}
                  </td>
                  <td style={{ fontWeight: 500 }}>
                    {s.material_nome || "—"}
                    {s.cor && <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 2 }}>{s.cor}</div>}
                  </td>
                  <td><span style={{ fontSize: 10, padding: "2px 8px", border: `1px solid var(--line)`, color: s.tipo === "peca" ? "var(--gold)" : "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".15em" }}>{s.tipo === "peca" ? "Peça" : "Retalho"}</span></td>
                  <td style={{ textAlign: "center", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>
                    {s.comprimento_cm && s.largura_cm ? `${s.comprimento_cm}×${s.largura_cm}cm · ` : ""}{s.area_m2} m²
                  </td>
                  <td style={{ fontSize: 12, color: "var(--ink-2)" }} data-testid={`sobra-marm-${s.id}`}>
                    {s.marmorista_nome || <span style={{ color: "var(--ink-3)", fontStyle: "italic" }}>—</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {s.setor_cor && <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: s.setor_cor, marginRight: 8, verticalAlign: "middle" }} />}
                    {s.local_estoque || "—"}
                  </td>
                  <td><StatusBadge status={s.status} /></td>
                  <td style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", color: "var(--gold)" }}>
                    {s.valor_estimado_m2 ? brl(s.valor_estimado_m2 * (s.area_m2 || 0)) : "—"}
                  </td>
                  {canEdit && (
                    <td>
                      {s.status === "disponivel" && (
                        <button className="btn btn-ghost btn-sm" style={{ marginRight: 4 }} onClick={() => setTransferring(s)} title="Transferir para outra obra" data-testid={`btn-transfer-${s.id}`}>
                          <ArrowRightLeft size={11} />
                        </button>
                      )}
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(s); setShowForm(true); }} style={{ marginRight: 4 }} data-testid={`btn-edit-sobra-${s.id}`}>Editar</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => del(s.id)} style={{ color: "#c47a7a" }}><Trash2 size={11} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <SobraFormModal
          obra={obra}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
      {transferring && (
        <SobraTransferModal
          sobra={transferring}
          obras={obras.filter(o => o.id !== obra.id && o.status !== "arquivada" && o.status !== "cancelada")}
          onClose={() => setTransferring(null)}
          onDone={() => { setTransferring(null); load(); }}
        />
      )}
    </div>
  );
}

function SobraFormModal({ obra, editing, onClose, onSaved }) {
  const [form, setForm] = useState({
    material_nome: editing?.material_nome || "",
    material_id: editing?.material_id || "",
    cor: editing?.cor || "",
    comprimento_cm: editing?.comprimento_cm || "",
    largura_cm: editing?.largura_cm || "",
    espessura_cm: editing?.espessura_cm || "",
    quantidade_m2: editing?.quantidade_m2 || "",
    quantidade_pecas: editing?.quantidade_pecas || 1,
    valor_estimado_m2: editing?.valor_estimado_m2 || "",
    local_estoque: editing?.local_estoque || "",
    setor_cor: editing?.setor_cor || SETOR_CORES[0].v,
    observacoes: editing?.observacoes || "",
    marmorista_id: editing?.marmorista_id || "",
    marmorista_nome: editing?.marmorista_nome || "",
  });
  const [busy, setBusy] = useState(false);
  const [materiais, setMateriais] = useState([]);
  const [marmoristas, setMarmoristas] = useState([]);
  const [foto, setFoto] = useState(editing?.foto_urls?.[0] || "");
  const [uploadingFoto, setUploadingFoto] = useState(false);

  useEffect(() => {
    authApi("get", "/materiais").then(setMateriais).catch(() => {});
    // Carrega terceirizados tipo=marmoraria para o seletor de marmorista
    authApi("get", "/terceirizados").then((all) => {
      const marms = (all || []).filter(t => (t.tipo || "").toLowerCase() === "marmoraria");
      setMarmoristas(marms);
      // Pré-preenche marmorista da obra (se ainda não houver) na criação
      if (!editing && !form.marmorista_id) {
        const obraMarmIds = (obra.terceirizados_ids || []);
        const obraMarm = marms.find(m => obraMarmIds.includes(m.id));
        if (obraMarm) {
          setForm(f => ({
            ...f,
            marmorista_id: obraMarm.id,
            marmorista_nome: obraMarm.empresa || obraMarm.nome,
          }));
        }
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMarmChange = (id) => {
    const m = marmoristas.find(x => x.id === id);
    setForm(f => ({
      ...f, marmorista_id: id,
      marmorista_nome: m ? (m.empresa || m.nome) : "",
    }));
  };

  const calcArea = () => {
    if (form.quantidade_m2) return parseFloat(form.quantidade_m2);
    const c = parseFloat(form.comprimento_cm) || 0;
    const l = parseFloat(form.largura_cm) || 0;
    return c && l ? +((c / 100) * (l / 100)).toFixed(3) : 0;
  };
  const area = calcArea();
  const tipoInferido = area >= 0.5 ? "peca" : "retalho";

  const onMatChange = (id) => {
    const m = materiais.find(x => x.id === id);
    setForm(f => ({ ...f, material_id: id, material_nome: m?.nome || "", valor_estimado_m2: m?.preco_custo || m?.preco_m2 || f.valor_estimado_m2 }));
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!editing?.id) {
      alert("Salve a sobra primeiro para adicionar foto.");
      return;
    }
    setUploadingFoto(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const token = localStorage.getItem("cps_token");
      const r = await axios.post(`${API}/obras/${obra.id}/sobras/${editing.id}/foto`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setFoto(r.data.url);
    } catch (er) { alert(er.response?.data?.detail || er.message); }
    finally { setUploadingFoto(false); }
  };

  const save = async () => {
    if (!form.material_nome) { alert("Material é obrigatório"); return; }
    setBusy(true);
    try {
      if (editing?.id) {
        await authApi("patch", `/obras/${obra.id}/sobras/${editing.id}`, form);
      } else {
        await authApi("post", `/obras/${obra.id}/sobras`, form);
      }
      onSaved();
    } catch (e) { alert(pickErrorMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-head">
          <h3 className="modal-title">{editing ? "Editar" : "Cadastrar"} <em>sobra</em></h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="field span2">
              <label>Material *</label>
              {materiais.length > 0 ? (
                <select value={form.material_id} onChange={e => onMatChange(e.target.value)} data-testid="sobra-material-select">
                  <option value="">— digitar manualmente —</option>
                  {materiais.map(m => <option key={m.id} value={m.id}>{m.nome}{m.cor ? ` · ${m.cor}` : ""}</option>)}
                </select>
              ) : null}
              <input
                style={{ marginTop: 6 }}
                placeholder="Nome do material (ex: Calacatta Oro)"
                value={form.material_nome}
                onChange={e => setForm({ ...form, material_nome: e.target.value })}
                data-testid="sobra-material-input"
              />
            </div>
            <div className="field"><label>Cor / Variação</label><input value={form.cor} onChange={e => setForm({ ...form, cor: e.target.value })} placeholder="Ex: Branco Carrara, Pietra Grey" data-testid="sobra-cor" /></div>
            <div className="field">
              <label>Marmorista</label>
              {marmoristas.length > 0 ? (
                <select value={form.marmorista_id} onChange={e => onMarmChange(e.target.value)} data-testid="sobra-marmorista-select">
                  <option value="">— sem marmorista —</option>
                  {marmoristas.map(m => <option key={m.id} value={m.id}>{m.empresa || m.nome}</option>)}
                </select>
              ) : (
                <input
                  value={form.marmorista_nome}
                  onChange={e => setForm({ ...form, marmorista_nome: e.target.value, marmorista_id: "" })}
                  placeholder="Onde fica fisicamente a sobra"
                  data-testid="sobra-marmorista-input"
                />
              )}
            </div>
            <div className="field"><label>Comprimento (cm)</label><input type="number" value={form.comprimento_cm} onChange={e => setForm({ ...form, comprimento_cm: e.target.value })} data-testid="sobra-comp" /></div>
            <div className="field"><label>Largura (cm)</label><input type="number" value={form.largura_cm} onChange={e => setForm({ ...form, largura_cm: e.target.value })} data-testid="sobra-larg" /></div>
            <div className="field"><label>Espessura (cm)</label><input type="number" step="0.5" value={form.espessura_cm} onChange={e => setForm({ ...form, espessura_cm: e.target.value })} /></div>
            <div className="field"><label>Quantidade m² (se retalho)</label><input type="number" step="0.01" value={form.quantidade_m2} onChange={e => setForm({ ...form, quantidade_m2: e.target.value })} placeholder="Use só para retalhos" /></div>
            <div className="field"><label>Qtd peças</label><input type="number" value={form.quantidade_pecas} onChange={e => setForm({ ...form, quantidade_pecas: e.target.value })} /></div>
            <div className="field"><label>Valor estimado/m² (R$)</label><input type="number" step="0.01" value={form.valor_estimado_m2} onChange={e => setForm({ ...form, valor_estimado_m2: e.target.value })} data-testid="sobra-valor" /></div>
            <div className="field span2">
              <label>Local de estoque</label>
              <input value={form.local_estoque} onChange={e => setForm({ ...form, local_estoque: e.target.value })} placeholder="Ex: Galpão A · Fileira 3 · Prateleira 2" data-testid="sobra-local" />
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {SETOR_CORES.map(sc => (
                  <button key={sc.v} type="button" onClick={() => setForm({ ...form, setor_cor: sc.v })} style={{
                    padding: "4px 10px", border: form.setor_cor === sc.v ? `2px solid ${sc.v}` : "1px solid var(--line)",
                    background: form.setor_cor === sc.v ? sc.v + "20" : "transparent",
                    fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase",
                    color: form.setor_cor === sc.v ? sc.v : "var(--ink-3)", cursor: "pointer",
                  }}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: sc.v, marginRight: 6 }} />
                    {sc.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="field span2"><label>Observações</label><textarea rows={2} value={form.observacoes} onChange={e => setForm({ ...form, observacoes: e.target.value })} placeholder="Estado, cor, defeitos, características úteis…" /></div>
            {editing?.id && (
              <div className="field span2">
                <label>Foto</label>
                {foto ? (
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <div style={{ width: 100, height: 70, backgroundImage: `url(${fileUrl(foto)})`, backgroundSize: "cover", border: "1px solid var(--gold-3)" }} />
                    <label className="btn btn-ghost btn-sm">{uploadingFoto ? "Enviando…" : "Trocar"}<input type="file" accept="image/*" hidden onChange={upload} /></label>
                  </div>
                ) : (
                  <label className="btn btn-ghost btn-sm" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <ImageIcon size={13} />{uploadingFoto ? "Enviando…" : "Adicionar foto"}
                    <input type="file" accept="image/*" hidden onChange={upload} />
                  </label>
                )}
              </div>
            )}
            <div className="field span2" style={{ padding: "10px 14px", background: "var(--bg-3)", fontSize: 12, color: "var(--ink-2)" }}>
              <strong style={{ color: "var(--gold)" }}>Resumo:</strong> {area} m² · classificada como <strong>{tipoInferido === "peca" ? "Peça" : "Retalho"}</strong>
              {form.valor_estimado_m2 && area > 0 && <> · valor: <strong>{brl(parseFloat(form.valor_estimado_m2) * area)}</strong></>}
            </div>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn" onClick={save} disabled={busy} data-testid="btn-save-sobra">{busy ? "Salvando…" : "Salvar"}</button>
        </div>
      </div>
    </div>
  );
}

function SobraTransferModal({ sobra, obras, onClose, onDone }) {
  const [obraDest, setObraDest] = useState("");
  const [valorM2, setValorM2] = useState(sobra.valor_estimado_m2 || "");
  const [busy, setBusy] = useState(false);

  const valorEcon = ((parseFloat(valorM2) || 0) * (sobra.area_m2 || 0)).toFixed(2);

  const transfer = async () => {
    if (!obraDest) { alert("Escolha a obra destino"); return; }
    setBusy(true);
    try {
      const r = await authApi("post", `/sobras/${sobra.id}/transferir`, { obra_destino_id: obraDest, valor_economia_m2: parseFloat(valorM2) || 0 });
      alert(`✓ Sobra transferida.\n\nEconomia registrada no DRE: ${brl(r.valor_economia)}`);
      onDone();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="modal-head">
          <h3 className="modal-title">Transferir <em>sobra</em></h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ padding: "10px 14px", background: "var(--bg-3)", marginBottom: 16, fontSize: 13 }}>
            <strong style={{ color: "var(--gold)" }}>{sobra.material_nome}</strong> · {sobra.area_m2} m² · da obra {sobra.obra_codigo}
          </div>
          <div className="field"><label>Obra destino *</label>
            <select value={obraDest} onChange={e => setObraDest(e.target.value)} data-testid="transfer-obra-select">
              <option value="">— selecione —</option>
              {obras.map(o => <option key={o.id} value={o.id}>{o.codigo} · {o.nome}</option>)}
            </select>
          </div>
          <div className="field"><label>Valor de economia por m² (R$)</label><input type="number" step="0.01" value={valorM2} onChange={e => setValorM2(e.target.value)} data-testid="transfer-valor" /></div>
          <div style={{ padding: "12px 14px", background: "rgba(58,90,74,0.12)", border: "1px solid rgba(58,90,74,0.3)", marginTop: 8, fontSize: 13, color: "var(--verde-2)" }}>
            <DollarSign size={14} style={{ display: "inline", verticalAlign: "middle", marginRight: 6 }} />
            Esta transferência criará um lançamento de <strong>economia de {brl(parseFloat(valorEcon))}</strong> no DRE da obra destino.
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn" onClick={transfer} disabled={busy} data-testid="btn-confirm-transfer">{busy ? "Transferindo…" : "Confirmar transferência"}</button>
        </div>
      </div>
    </div>
  );
}


/* =========================================================================
 * SOBRAS GLOBAL PAGE — /sobras
 * Resumo consolidado + lista filtrada
 * ========================================================================= */
export function SobrasGlobalPage() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState("disponivel");
  const [filterMaterial, setFilterMaterial] = useState("");
  const [filterMarmorista, setFilterMarmorista] = useState("");
  const [filterCliente, setFilterCliente] = useState("");
  const [marmoristas, setMarmoristas] = useState([]);

  // Lê query string ?cliente=X para pré-filtrar (deep link do alerta da obra)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const c = params.get("cliente");
    if (c) {
      setFilterCliente(c);
      setFilterStatus("disponivel");
    }
  }, []);

  const load = useCallback(async () => {
    const [s, lst, terc] = await Promise.all([
      authApi("get", "/sobras/summary"),
      authApi("get", "/sobras"),
      authApi("get", "/terceirizados"),
    ]);
    setSummary(s);
    setItems(lst.items || []);
    setMarmoristas((terc || []).filter(t => (t.tipo || "").toLowerCase() === "marmoraria"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const norm = (v) => (v || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filtered = items.filter(s => {
    if (filterStatus !== "todas" && s.status !== filterStatus) return false;
    if (filterMaterial) {
      const q = norm(filterMaterial);
      const hit = norm(s.material_nome).includes(q) || norm(s.cor).includes(q);
      if (!hit) return false;
    }
    if (filterMarmorista && s.marmorista_id !== filterMarmorista) return false;
    if (filterCliente) {
      const q = norm(filterCliente);
      if (!norm(s.cliente_nome).includes(q)) return false;
    }
    return true;
  });

  if (!summary) return <div className="container" style={{ paddingTop: 80 }}><p className="page-sub">Carregando…</p></div>;

  return (
    <div className="container">
      <div className="page-head fade-up">
        <div>
          <div className="eyebrow"><Recycle size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--gold)" }} /> Reaproveitamento</div>
          <h1 className="page-title">Estoque de <em>Sobras</em></h1>
          <p className="page-sub">Peças e retalhos disponíveis para reaproveitar em outras obras ou vender.</p>
        </div>
      </div>

      {/* KPIs principais */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 24 }}>
        <div className="panel" style={{ padding: 20, textAlign: "center", border: "1px solid var(--gold-3)" }} data-testid="sobras-kpi-disponivel">
          <div className="eyebrow" style={{ marginBottom: 8 }}>Disponível agora</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 36, fontStyle: "italic", color: "var(--gold)" }}>{summary.by_status.disponivel.area_m2} m²</div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>{summary.by_status.disponivel.count} peças · {brl(summary.by_status.disponivel.valor)}</div>
        </div>
        <div className="panel" style={{ padding: 20, textAlign: "center" }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Economia já gerada</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 36, fontStyle: "italic", color: "var(--verde-2)" }}>{brl(summary.total_economia_dre)}</div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>{summary.by_status.transferida.count} transferências</div>
        </div>
        <div className="panel" style={{ padding: 20, textAlign: "center" }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Receita por venda</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 36, fontStyle: "italic", color: "#d4ac5a" }}>{brl(summary.total_vendido)}</div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>{summary.by_status.vendida.count} vendas</div>
        </div>
        <div className="panel" style={{ padding: 20, textAlign: "center" }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Total cadastrado</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 36, fontStyle: "italic", color: "var(--ink-1)" }}>{summary.total_sobras}</div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>{summary.by_material.length} materiais distintos</div>
        </div>
      </div>

      {/* Por material */}
      {summary.by_material.length > 0 && (
        <div className="panel" style={{ padding: 20, marginBottom: 24 }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>Disponível por material</div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Material</th>
                  <th style={{ textAlign: "center" }}>Disponível</th>
                  <th style={{ textAlign: "center" }}>Total</th>
                  <th style={{ textAlign: "right" }}>Valor disponível</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_material.map(m => (
                  <tr key={m.material_id || m.material_nome} data-testid={`mat-row-${m.material_id || m.material_nome}`}>
                    <td style={{ fontWeight: 500 }}>{m.material_nome}</td>
                    <td style={{ textAlign: "center", color: "var(--gold)", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{m.area_disponivel} m² <span style={{ fontSize: 10, color: "var(--ink-3)", fontStyle: "normal" }}>({m.count_disponivel} peças)</span></td>
                    <td style={{ textAlign: "center", color: "var(--ink-2)" }}>{m.area_total} m² <span style={{ fontSize: 10, color: "var(--ink-3)" }}>({m.count_total} peças)</span></td>
                    <td style={{ textAlign: "right", color: "var(--verde-2)", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{brl(m.valor_disponivel)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filtros + lista */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        {[
          ["todas", "Todas"], ["disponivel", "Disponíveis"], ["reservada", "Reservadas"],
          ["transferida", "Transferidas"], ["vendida", "Vendidas"], ["descartada", "Descartadas"],
        ].map(([k, lbl]) => (
          <button key={k} className={"chip " + (filterStatus === k ? "active" : "")} onClick={() => setFilterStatus(k)} data-testid={`sobras-filter-${k}`}>{lbl}</button>
        ))}
        <input
          placeholder="Buscar material ou cor…"
          value={filterMaterial}
          onChange={e => setFilterMaterial(e.target.value)}
          style={{ flex: 1, minWidth: 180, padding: "8px 12px", background: "var(--bg-3)", border: "1px solid var(--line)", color: "var(--ink-1)", marginLeft: 8 }}
          data-testid="sobras-search"
        />
        {marmoristas.length > 0 && (
          <select
            value={filterMarmorista}
            onChange={e => setFilterMarmorista(e.target.value)}
            data-testid="sobras-filter-marmorista"
            style={{ padding: "8px 12px", background: "var(--bg-3)", border: "1px solid var(--line)", color: "var(--ink-1)" }}
          >
            <option value="">Marmorista (todos)</option>
            {marmoristas.map(m => <option key={m.id} value={m.id}>{m.empresa || m.nome}</option>)}
          </select>
        )}
        <input
          placeholder="Cliente da obra…"
          value={filterCliente}
          onChange={e => setFilterCliente(e.target.value)}
          style={{ minWidth: 180, padding: "8px 12px", background: "var(--bg-3)", border: "1px solid var(--line)", color: "var(--ink-1)" }}
          data-testid="sobras-filter-cliente"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="empty"><h3>Nenhuma sobra {filterStatus !== "todas" ? "neste filtro" : "cadastrada"}</h3></div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl" data-testid="sobras-global-table">
            <thead>
              <tr>
                <th>Foto</th>
                <th>Material</th>
                <th>Obra origem</th>
                <th>Cliente</th>
                <th>Marmorista</th>
                <th style={{ textAlign: "center" }}>m²</th>
                <th>Local</th>
                <th>Status</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} data-testid={`global-sobra-${s.id}`}>
                  <td>
                    {s.foto_urls?.[0] ? (
                      <div style={{ width: 50, height: 38, backgroundImage: `url(${fileUrl(s.foto_urls[0])})`, backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--gold-3)" }} />
                    ) : <div style={{ width: 50, height: 38, background: "var(--bg-3)", border: "1px solid var(--line)" }} />}
                  </td>
                  <td style={{ fontWeight: 500 }}>
                    {s.material_nome}
                    {s.cor && <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 2 }}>{s.cor}</div>}
                    <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 2 }}>{s.tipo === "peca" ? "Peça" : "Retalho"}</div>
                  </td>
                  <td style={{ fontSize: 12, color: "var(--ink-2)" }}>{s.obra_codigo} · {s.obra_nome}</td>
                  <td style={{ fontSize: 12, color: "var(--ink-2)" }} data-testid={`global-sobra-cliente-${s.id}`}>{s.cliente_nome || "—"}</td>
                  <td style={{ fontSize: 12, color: "var(--ink-2)" }} data-testid={`global-sobra-marm-${s.id}`}>{s.marmorista_nome || "—"}</td>
                  <td style={{ textAlign: "center", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>{s.area_m2}</td>
                  <td style={{ fontSize: 12 }}>
                    {s.setor_cor && <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: s.setor_cor, marginRight: 8, verticalAlign: "middle" }} />}
                    {s.local_estoque || "—"}
                  </td>
                  <td><StatusBadge status={s.status} /></td>
                  <td style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", color: "var(--gold)" }}>
                    {s.valor_estimado_m2 ? brl(s.valor_estimado_m2 * (s.area_m2 || 0)) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* =========================================================================
 * SOBRAS ALERTA CLIENTE — banner amarelo na obra quando cliente tem
 * sobras disponíveis de obras anteriores. Visível apenas para usuários
 * internos autenticados — NUNCA aparece no Signature Book público.
 * ========================================================================= */
export function SobrasAlertaCliente({ obra }) {
  const [data, setData] = useState(null);
  const clienteNome = obra?.cliente_nome;
  useEffect(() => {
    if (!clienteNome || !obra?.id) return;
    const params = new URLSearchParams({
      cliente_nome: clienteNome,
      obra_id_excluir: obra.id,
    });
    authApi("get", `/sobras/alerta-cliente?${params.toString()}`)
      .then(setData).catch(() => setData(null));
  }, [clienteNome, obra?.id]);

  if (!data || !data.items?.length) return null;

  const totalM2 = data.items.reduce((s, x) => s + (x.area_m2 || 0), 0);
  const obrasOrigem = Array.from(new Set(data.items.map(x => x.obra_nome).filter(Boolean)));
  const link = `/sobras?cliente=${encodeURIComponent(clienteNome)}`;

  return (
    <div
      data-testid="sobras-alerta-cliente"
      style={{
        padding: "14px 18px",
        background: "linear-gradient(90deg, rgba(212,178,122,0.10) 0%, rgba(184,147,90,0.04) 100%)",
        border: "1px solid rgba(212,178,122,0.45)",
        borderLeft: "3px solid #d4b27a",
        marginBottom: 18,
        display: "flex",
        gap: 14,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <div style={{ fontSize: 22 }}>⚠️</div>
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{ fontSize: 10, letterSpacing: ".22em", textTransform: "uppercase", color: "#d4b27a", marginBottom: 4 }}>
          Atenção · Cliente recorrente
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-1)", lineHeight: 1.55 }}>
          Existem <strong style={{ color: "#d4b27a" }} data-testid="sobras-alerta-count">{data.total} sobra(s) disponíveis</strong>{" "}
          ({totalM2.toFixed(2)} m²) de obras anteriores de <strong>{clienteNome}</strong>
          {obrasOrigem.length > 0 && (
            <span style={{ color: "var(--ink-3)" }}> · {obrasOrigem.slice(0, 3).join(" · ")}{obrasOrigem.length > 3 ? ` +${obrasOrigem.length - 3}` : ""}</span>
          )}.
          <br />
          <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
            Considere reaproveitar antes de comprar material novo.
          </span>
        </div>
      </div>
      <a
        href={link}
        className="btn btn-ghost btn-sm"
        data-testid="sobras-alerta-ver"
        style={{ borderColor: "#d4b27a", color: "#d4b27a" }}
      >
        Ver sobras
      </a>
    </div>
  );
}


/* =========================================================================
 * SOBRAS SUGGESTIONS — embedded in obra detail when materiais exist
 * ========================================================================= */
export function SobrasSugestoes({ obraId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    authApi("get", `/sobras/sugestoes/${obraId}`).then(setData).catch(() => {});
  }, [obraId]);
  if (!data || !data.items?.length) return null;

  return (
    <div className="panel" style={{ padding: 18, marginBottom: 16, border: "1px solid var(--gold-3)", background: "linear-gradient(180deg, rgba(184,147,90,0.06) 0%, transparent 100%)" }} data-testid="sobras-sugestoes">
      <div className="eyebrow" style={{ marginBottom: 10 }}><Package size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--gold)" }} />Sugestões de reaproveitamento</div>
      <p style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 12 }}>
        Existem <strong style={{ color: "var(--gold)" }}>{data.total} sobras compatíveis</strong> em outras obras com materiais já vinculados a esta obra.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
        {data.items.slice(0, 6).map(s => (
          <div key={s.id} style={{ padding: 12, background: "var(--bg-3)", border: "1px solid var(--line)" }}>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 14, color: "var(--gold)" }}>{s.material_nome}</div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>{s.obra_codigo} · {s.area_m2} m² · {s.tipo}</div>
            {s.local_estoque && <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>📍 {s.local_estoque}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
